import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Pin, Send, Undo2, ExternalLink, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RichEditor } from "@/components/rich/RichEditor";
import { api, errMsg } from "@/lib/api";
import { TAGS, fmtDate } from "@/lib/siteNews";
import { ImageField, InsertImageButton } from "@/components/admin/siteNews/ImageField";

const refresh = (qc) => { qc.invalidateQueries({ queryKey: ["admin-news"] }); qc.invalidateQueries({ queryKey: ["site-updates"] }); qc.invalidateQueries({ queryKey: ["news-latest"] }); };

const PostDialog = ({ post, onClose }) => {
  const qc = useQueryClient();
  const [f, setF] = useState({ title: "", body: "", tag: "update", cover_id: null, pinned: false, ...post });
  const [ed, setEd] = useState(null);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    const body = { title: f.title.trim(), body: f.body, tag: f.tag, cover_id: f.cover_id, pinned: f.pinned };
    try {
      if (post?.id) await api.patch(`/admin/site/news/${post.id}`, body); else await api.post("/admin/site/news", body);
      refresh(qc);
      toast.success(post?.id ? "Post saved" : "Draft saved. Publish it from the list.");
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="news-post-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{post?.id ? "Edit post" : "New post"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Input value={f.title} onChange={(e) => set("title")(e.target.value)} maxLength={160} placeholder="Headline" className="font-display text-xl" data-testid="news-post-title" />
          <div className="flex flex-wrap items-center gap-3">
            <Select value={f.tag} onValueChange={set("tag")}>
              <SelectTrigger className="w-48" data-testid="news-post-tag"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(TAGS).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-sm text-[#E8DFD1]"><Switch checked={f.pinned} onCheckedChange={set("pinned")} data-testid="news-post-pinned" />Pin to top</label>
          </div>
          <ImageField value={f.cover_id} onChange={set("cover_id")} testid="news-post-cover" label="Cover picture" />
          <div className="rounded border border-[#2D3442]">
            <RichEditor value={f.body} onChange={set("body")} images onEditor={setEd} placeholder="Write your update…" minH="min-h-[260px]" testid="news-post-body" extra={<InsertImageButton editor={ed} testid="news-post-insert-image" />} />
          </div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={!f.title.trim()} data-testid="news-post-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PublishDialog = ({ post, onClose }) => {
  const qc = useQueryClient();
  const [email, setEmail] = useState(false);
  const { data: aud } = useQuery({ queryKey: ["admin-audience"], queryFn: () => api.get("/admin/announcements/audience").then((r) => r.data) });
  const go = async () => {
    try {
      const { data } = await api.post(`/admin/site/news/${post.id}/publish`, { publish: true, email });
      refresh(qc);
      toast.success(data.email_total ? `Published. Emailing ${data.email_total} users in the background.` : "Published");
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="news-publish-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Publish “{post.title}”</DialogTitle></DialogHeader>
        <p className="text-sm text-[#A8A090]">It appears on the Building LoreRight page right away, and users see a dot on What's new.</p>
        {post.emailed ? <p className="flex items-center gap-1.5 text-sm text-[#A8A090]" data-testid="news-publish-already-emailed"><Mail className="h-4 w-4" />Already emailed once, so it won't be sent again.</p> : (
          <label className="flex items-start gap-2 text-sm text-[#E8DFD1]">
            <Checkbox checked={email} onCheckedChange={(v) => setEmail(!!v)} className="mt-0.5" data-testid="news-publish-email" />
            <span>Email this to all users{aud ? ` (${aud.all})` : ""}<span className="block text-xs text-[#A8A090]">Skips anyone who unsubscribed. Sends the text with a link to the full post.</span></span>
          </label>
        )}
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={go} data-testid="news-publish-confirm"><Send className="mr-1 h-4 w-4" />Publish</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PostRow = ({ p, onEdit, onPublish }) => {
  const qc = useQueryClient();
  const act = async (fn, ok) => { try { await fn(); refresh(qc); toast.success(ok); } catch (e) { toast.error(errMsg(e)); } };
  const pub = p.status === "published";
  const [label, color] = TAGS[p.tag] || TAGS.update;
  return (
    <div className="flex items-center gap-3 rounded border border-[#2D3442] bg-[#0F131A] p-2.5" data-testid={`news-row-${p.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 truncate text-sm text-[#F3EBDD]">{p.pinned && <Pin className="h-3.5 w-3.5 text-[#F59E0B]" />}{p.title}</div>
        <div className="text-[11px] text-[#A8A090]"><span style={{ color }}>{label}</span> · <span data-testid={`news-status-${p.id}`}>{pub ? `Published ${fmtDate(p.published_at)}` : "Draft"}</span>{p.emailed && " · Emailed"}</div>
      </div>
      {pub ? <Button size="sm" variant="ghost" onClick={() => act(() => api.post(`/admin/site/news/${p.id}/publish`, { publish: false }), "Moved back to drafts")} data-testid={`news-unpublish-${p.id}`}><Undo2 className="mr-1 h-3.5 w-3.5" />Unpublish</Button>
        : <Button size="sm" onClick={onPublish} data-testid={`news-publish-${p.id}`}><Send className="mr-1 h-3.5 w-3.5" />Publish</Button>}
      {pub && <a href={`/updates#post-${p.id}`} target="_blank" rel="noreferrer" className="rounded p-1.5 text-[#A8A090] hover:text-[#F3EBDD]" data-testid={`news-view-${p.id}`}><ExternalLink className="h-4 w-4" /></a>}
      <button onClick={onEdit} className="rounded p-1.5 text-[#A8A090] hover:bg-[#1A202C] hover:text-[#F3EBDD]" data-testid={`news-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
      <button onClick={() => window.confirm(`Delete “${p.title}”?`) && act(() => api.delete(`/admin/site/news/${p.id}`), "Post deleted")} className="rounded p-1.5 text-[#A8A090] hover:bg-[#1A202C] hover:text-[#F87171]" data-testid={`news-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
    </div>
  );
};

export const NewsAdmin = () => {
  const { data: posts = [] } = useQuery({ queryKey: ["admin-news"], queryFn: () => api.get("/admin/site/news").then((r) => r.data) });
  const [edit, setEdit] = useState(null);
  const [publish, setPublish] = useState(null);
  return (
    <section className="rounded-md border border-[#2D3442] bg-[#13171F] p-6" data-testid="news-admin">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div><h3 className="font-display text-2xl text-[#F3EBDD]">News & updates</h3><p className="text-sm text-[#A8A090]">Announce features, updates and events like free access during NaNoWriMo.</p></div>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" asChild><a href="/updates" target="_blank" rel="noreferrer" data-testid="news-open-page"><ExternalLink className="mr-1 h-4 w-4" />View page</a></Button>
          <Button size="sm" onClick={() => setEdit({})} data-testid="news-new"><Plus className="mr-1 h-4 w-4" />New post</Button>
        </div>
      </div>
      {!posts.length && <p className="text-sm text-[#A8A090]" data-testid="news-admin-empty">No posts yet.</p>}
      <div className="space-y-2">{posts.map((p) => <PostRow key={p.id} p={p} onEdit={() => setEdit(p)} onPublish={() => setPublish(p)} />)}</div>
      {edit && <PostDialog post={edit} onClose={() => setEdit(null)} />}
      {publish && <PublishDialog post={publish} onClose={() => setPublish(null)} />}
    </section>
  );
};
